<template>
	<header id="header" class="app-header organizer-app-header">
		<div class="mobile-toggler">
			<button
				v-if="userRole !== 'Visitor'"
				type="button"
				class="menu-toggler topnav-hamburger"
				:aria-label="$t('topbar.navigation.toggle' /* 切換導覽列 */)"
				@click="toggleHamburgerMenu"
			>
				<span class="hamburger-icon">
					<span class="bar"></span>
					<span class="bar"></span>
				</span>
			</button>
		</div>

		<div class="brand organizer-app-brand">
			<div class="desktop-toggler">
				<button
					v-if="userRole !== 'Visitor'"
					type="button"
					class="menu-toggler topnav-hamburger"
					id="topnav-hamburger-icon"
					:aria-label="
						$t('topbar.navigation.toggle' /* 切換導覽列 */)
					"
					@click="toggleHamburgerMenu"
				>
					<span class="hamburger-icon">
						<span class="bar"></span>
						<span class="bar"></span>
					</span>
				</button>
			</div>

			<router-link
				to="/"
				class="brand-logo"
				:aria-label="
					$t('topbar.navigation.home' /* YouEvent365 首頁 */)
				"
			>
				<span class="brand-logo-mark">
					<BrandLogo variant="dark" size="compact" />
				</span>
				<span class="brand-logo-full">
					<BrandLogo variant="dark" size="sidebar" />
				</span>
			</router-link>
		</div>

		<div class="menu organizer-app-menu">
			<!-- §3 搜尋列（Visitor 隱藏） -->
			<form
				v-if="userRole !== 'Visitor'"
				class="menu-search d-none d-md-flex"
				ref="searchWrapper"
				@submit.prevent="handleSearchSubmit"
			>
				<div class="position-relative">
					<input
						type="text"
						class="form-control"
						:placeholder="
							$t('topbar.search.placeholder' /* 搜尋功能頁面 */)
						"
						id="search-options"
						v-model="searchValue"
						autocomplete="off"
						@focus="openSearchDropdown"
						@input="handleSearchInput"
						@keydown.enter.prevent="handleSearchSubmit"
					/>
					<span class="mdi mdi-magnify search-widget-icon"></span>
					<span
						class="mdi mdi-close-circle search-widget-icon search-widget-icon-close"
						:id="'search-close-options'"
						:class="{ 'd-none': !searchOpen }"
						@click="clearSearch"
					></span>
					<div
						class="dropdown-menu dropdown-menu-lg"
						:id="'search-dropdown'"
						:class="{ show: searchOpen }"
					>
						<div
							v-if="recentSearchResults.length > 0"
							class="dropdown-header mt-2"
						>
							<h6
								class="text-overflow text-muted mb-1 text-uppercase"
							>
								{{ $t('topbar.search.recent' /* 最近搜尋 */) }}
							</h6>
						</div>
						<div
							v-if="recentSearchResults.length > 0"
							class="dropdown-item bg-transparent text-wrap search-recent-list"
						>
							<span
								v-for="result in recentSearchResults"
								:key="`recent-${result.routeName}`"
								class="search-recent-chip me-1 mb-1"
							>
								<button
									type="button"
									class="search-recent-chip-link"
									@click="handleMenuSearchSelect(result)"
								>
									{{ result.labelCurrent }}
									<i class="mdi mdi-magnify ms-1"></i>
								</button>
								<button
									type="button"
									class="search-recent-chip-remove"
									:aria-label="
										$t(
											'topbar.search.removeRecent' /* 移除最近搜尋 */,
											{ label: result.labelCurrent }
										)
									"
									@click.stop="
										removeRecentSearchRoute(
											result.routeName
										)
									"
								>
									<i class="mdi mdi-close"></i>
								</button>
							</span>
						</div>
						<div class="dropdown-header mt-2">
							<h6
								class="text-overflow text-muted mb-1 text-uppercase"
							>
								{{
									$t(
										'topbar.search.featurePages' /* 功能頁面 */
									)
								}}
							</h6>
						</div>
						<template v-if="menuSearchResults.length > 0">
							<button
								v-for="result in menuSearchResults"
								:key="result.routeName"
								type="button"
								class="dropdown-item notify-item search-result-item"
								@click="handleMenuSearchSelect(result)"
							>
								<span class="search-result-icon">
									<i
										class="ri-file-list-3-line fs-18 text-muted"
									></i>
								</span>
								<span class="search-result-body">
									<span class="search-result-label">{{
										result.labelCurrent
									}}</span>
									<small
										v-if="result.contextCurrent"
										class="search-result-context text-muted fs-11"
									>
										{{ result.contextCurrent }}
									</small>
								</span>
							</button>
						</template>
						<div
							v-else-if="searchValue.trim().length > 0"
							class="dropdown-item text-muted py-3 search-dropdown-empty-state"
						>
							{{
								$t(
									'topbar.search.noResults' /* 找不到符合的功能頁面 */
								)
							}}
						</div>
						<div
							v-else
							class="dropdown-item text-muted py-3 search-dropdown-empty-state"
						>
							{{
								$t('topbar.search.hint' /* 輸入功能頁面名稱 */)
							}}
						</div>
					</div>
				</div>
			</form>

			<GlobalEventSelector v-if="userRole !== 'Visitor'" />

			<div class="menu-actions d-flex align-items-center">
				<BDropdown
					v-if="userRole !== 'Visitor'"
					class="menu-item dropdown d-md-none topbar-head-dropdown header-item"
					variant="ghost-secondary"
					dropstart
					:no-caret="true"
					:offset="{ alignmentAxis: 55, crossAxis: 15, mainAxis: 0 }"
					toggle-class="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle material-shadow-none show menu-link"
					menu-class="dropdown-menu-lg dropdown-menu-end p-0"
				>
					<template #button-content>
						<i class="bx bx-search fs-22"></i>
					</template>
					<BDropdownItem
						aria-labelledby="page-header-search-dropdown"
					>
						<form class="p-3" @submit.prevent="handleSearchSubmit">
							<div class="m-0">
								<div class="input-group">
									<input
										v-model="searchValue"
										type="text"
										class="form-control"
										:placeholder="
											$t(
												'topbar.search.placeholder' /* 搜尋功能頁面 */
											)
										"
									/>
									<BButton variant="primary" type="submit">
										<i class="mdi mdi-magnify"></i>
									</BButton>
								</div>
							</div>
						</form>
					</BDropdownItem>
				</BDropdown>

				<!-- 畫面語系切換 -->
				<BDropdown
					class="menu-item dropdown language-selector-item header-item"
					variant="link"
					:no-caret="true"
					toggle-class="menu-link language-selector-link btn-topbar btn-ghost-secondary rounded-circle material-shadow-none"
					menu-class="dropdown-menu-end"
					:aria-label="
						$t('topbar.language.change' /* 切換畫面語系 */)
					"
				>
					<template #button-content>
						<span class="menu-icon">
							<img
								:src="selectedLanguage.flag"
								:alt="$t(selectedLanguage.labelKey)"
								class="rounded-circle"
								height="20"
								width="20"
							/>
						</span>
						<span class="visually-hidden">
							{{
								$t('topbar.language.current' /* 目前語系 */)
							}}：{{ $t(selectedLanguage.labelKey) }}
						</span>
					</template>

					<div class="dropdown-header">
						{{ $t('topbar.language.title' /* 畫面語系 */) }}
					</div>
					<BDropdownItem
						v-for="language in languages"
						:key="language.value"
						:active="selectedLanguage.value === language.value"
						@click="setLanguage(language)"
					>
						<span class="d-flex align-items-center gap-2">
							<img
								:src="language.flag"
								:alt="$t(language.labelKey)"
								class="rounded-circle"
								height="18"
								width="18"
							/>
							<span class="flex-grow-1">{{
								$t(language.labelKey)
							}}</span>
							<i
								v-if="selectedLanguage.value === language.value"
								class="ri-check-line"
								aria-hidden="true"
							></i>
						</span>
					</BDropdownItem>
				</BDropdown>

				<!-- 深色／淺色模式切換 -->
				<div class="menu-item header-item theme-mode-item d-none d-sm-flex">
					<BButton
						type="button"
						variant="ghost-secondary"
						class="menu-link theme-mode-toggle btn-icon btn-topbar btn-ghost-secondary material-shadow-none"
						:aria-label="
							$t(
								isDarkMode
									? 'account-settings.preferences.themeOptions.light'
									: 'account-settings.preferences.themeOptions.dark'
							)
						"
						:title="
							$t(
								isDarkMode
									? 'account-settings.preferences.themeOptions.light'
									: 'account-settings.preferences.themeOptions.dark'
							)
						"
						:aria-pressed="isDarkMode"
						@click="toggleDarkMode"
					>
						<span class="menu-icon">
							<i
								:class="
								isDarkMode
									? 'ri-sun-line'
									: 'ri-moon-line'
								"
								aria-hidden="true"
							></i>
						</span>
					</BButton>
				</div>

				<!-- Vue Studio header action：通知 -->
				<BDropdown
					v-if="userRole !== 'Visitor' && showTopbarNotifications"
					class="menu-item dropdown studio-notification-item"
					variant="link"
					dropstart
					:no-caret="true"
					:offset="{ alignmentAxis: 57, crossAxis: 0, mainAxis: -52 }"
					toggle-class="menu-link studio-notification-link"
					id="page-header-notifications-dropdown"
					menu-class="dropdown-menu-lg dropdown-menu-end p-0"
				>
					<template #button-content>
						<span class="menu-icon">
							<i class="ri-notification-3-line"></i>
						</span>
						<span v-if="notificationCount > 0" class="menu-label">
							{{ notificationCount }}
						</span>
					</template>

					<!-- 平台公告標題區塊 -->
					<div
						class="dropdown-head bg-primary bg-pattern rounded-top"
					>
						<div class="p-3">
							<div class="d-flex align-items-center">
								<h6
									class="m-0 fs-16 fw-semibold text-white flex-grow-1"
								>
									{{ $t('topbar.notifications.title') }}
								</h6>
								<span
									v-if="notificationCount > 0"
									class="badge bg-light-subtle text-body fs-13"
								>
									{{
										$t('topbar.notifications.count', {
											count: notificationCount,
										})
									}}
								</span>
							</div>
						</div>
					</div>

					<!-- 平台公告內容 -->
					<div class="tab-content">
						<div class="tab-pane active">
							<SimpleBar style="max-height: 300px">
								<div
									v-if="notificationsLoading"
									class="text-center text-muted py-5 px-3"
								>
									<span
										class="spinner-border spinner-border-sm me-2"
										aria-hidden="true"
									></span>
									{{ $t('topbar.notifications.loading') }}
								</div>
								<div
									v-else-if="notificationsError"
									class="text-center py-5 px-3"
								>
									<i
										class="ri-error-warning-line fs-32 text-danger"
										aria-hidden="true"
									></i>
									<p class="text-muted mt-2 mb-3">
										{{
											$t('topbar.notifications.loadError')
										}}
									</p>
									<button
										type="button"
										class="btn btn-sm btn-outline-primary"
										@click="loadNotifications"
									>
										{{ $t('topbar.notifications.retry') }}
									</button>
								</div>
								<div
									v-else-if="notifications.length === 0"
									class="text-center py-5 px-3"
								>
									<i
										class="bx bx-bell-off fs-48 text-muted"
										aria-hidden="true"
									></i>
									<p class="text-muted mt-2 mb-0">
										{{ $t('topbar.notifications.empty') }}
									</p>
								</div>
								<div v-else>
									<button
										v-for="item in notifications"
										:key="item.objid"
										type="button"
										class="platform-notification-row d-flex align-items-start gap-2 w-100 py-3 px-3 border-0 border-bottom bg-transparent text-start"
										@click="openNotification(item)"
									>
										<span class="avatar-xs flex-shrink-0">
											<span
												class="avatar-title rounded-circle bg-info-subtle text-info"
											>
												<i
													class="ri-megaphone-line"
													aria-hidden="true"
												></i>
											</span>
										</span>
										<span class="min-w-0 flex-grow-1">
											<span
												class="d-block fw-semibold fs-13 text-body text-truncate"
												:title="item.subject"
											>
												{{ item.subject }}
											</span>
											<span
												class="d-block text-muted fs-11 mt-1"
											>
												{{
													formatNotificationTime(
														item.publishTime
													)
												}}
											</span>
										</span>
									</button>
								</div>
							</SimpleBar>
						</div>
					</div>
				</BDropdown>

				<!-- Vue Studio header action：使用者選單 -->
				<BDropdown
					v-if="userRole !== 'Visitor'"
					class="menu-item user-menu-item header-item"
					variant="link"
					id="page-header-user-dropdown"
					toggle-class="menu-link user-menu-link"
					menu-class="dropdown-menu-end"
					:no-caret="true"
					:offset="{
						alignmentAxis: -14,
						crossAxis: 0,
						mainAxis: 0,
					}"
				>
					<template #button-content>
						<span class="menu-img online">
							<img
								src="@/assets/images/users/avatar-4.jpg"
								alt="Header Avatar"
								class="rounded-circle"
							/>
						</span>
						<span class="menu-text d-none d-lg-inline">
							{{ userName }}
						</span>
					</template>

					<!-- 歡迎標題 -->
					<div class="dropdown-header noti-title">
						<h6 class="text-overflow m-0">
							{{
								$t('topbar.profile.welcome' /* 歡迎回來 */, {
									name: userName,
								})
							}}
						</h6>
					</div>

					<!-- 依角色渲染選單項目 -->
					<template
						v-for="item in profileMenuItems"
						:key="item.label"
					>
						<div v-if="item.divider" class="dropdown-divider"></div>
						<BDropdownItem v-else :to="item.route || undefined">
							<i
								:class="`mdi ${item.icon} text-muted fs-16 align-middle me-1`"
							></i>
							<span class="align-middle">{{ item.label }}</span>
							<!-- 留抵金餘額顯示 -->
							<span
								v-if="item.balance !== undefined"
								class="badge bg-success-subtle text-success ms-auto"
							>
								NT${{ item.balance.toLocaleString() }}
							</span>
						</BDropdownItem>
					</template>
				</BDropdown>
			</div>
		</div>
	</header>

	<!-- 平台公告完整內容 Modal -->
	<teleport to="body">
		<div
			v-if="showNotificationModal && selectedNotification"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			style="background: rgba(0, 0, 0, 0.5)"
			@click.self="closeNotification"
		>
			<div
				class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
			>
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							{{ $t('topbar.notifications.title') }}
						</h5>
						<button
							type="button"
							class="btn-close"
							:aria-label="$t('topbar.notifications.close')"
							@click="closeNotification"
						></button>
					</div>
					<div class="modal-body">
						<p class="text-muted fs-12 mb-3">
							{{
								$t('topbar.notifications.publishedAt', {
									time: formatNotificationTime(
										selectedNotification.publishTime
									),
								})
							}}
						</p>
						<div class="platform-notification-content">
							{{ selectedNotification.content }}
						</div>
					</div>
					<div class="modal-footer">
						<button
							type="button"
							class="btn btn-primary"
							@click="closeNotification"
						>
							{{ $t('topbar.notifications.close') }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</teleport>
</template>

<style scoped>
.dropdown-head.bg-pattern {
	background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h20v20H0z' fill='none'/%3E%3Ccircle cx='2' cy='2' r='1' fill='rgba(255,255,255,0.1)'/%3E%3C/svg%3E");
}
.nav-tabs-custom .nav-link {
	border: none;
	padding: 0.5rem 1rem;
	opacity: 0.8;
}
.nav-tabs-custom .nav-link.active {
	opacity: 1;
	background: transparent;
	border-bottom: 2px solid #fff;
}
.min-w-0 {
	min-width: 0;
}
.platform-notification-row {
	cursor: pointer;
}
.platform-notification-row:hover,
.platform-notification-row:focus-visible {
	background: var(--vz-light) !important;
	outline: none;
}
.platform-notification-content {
	white-space: pre-wrap;
	word-break: break-word;
	line-height: 1.7;
}
.modal-title {
	word-break: break-word;
}
#search-dropdown {
	max-width: min(28rem, calc(100vw - 2rem));
}
.search-recent-list,
.search-result-item,
.search-dropdown-empty-state {
	white-space: normal;
}
.search-recent-chip {
	display: inline-flex;
	align-items: stretch;
	overflow: hidden;
	border-radius: 999px;
	background: rgba(64, 129, 255, 0.12);
}
.search-recent-chip-link,
.search-recent-chip-remove {
	border: 0;
	background: transparent;
	color: var(--vz-primary);
}
.search-recent-chip-link {
	display: inline-flex;
	align-items: center;
	padding: 0.35rem 0.35rem 0.35rem 0.75rem;
	font-size: 0.765625rem;
	line-height: 1.2;
}
.search-recent-chip-remove {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0 0.55rem 0 0.15rem;
	font-size: 0.9rem;
}
.search-recent-chip-link:hover,
.search-recent-chip-link:focus,
.search-recent-chip-remove:hover,
.search-recent-chip-remove:focus {
	background: rgba(64, 129, 255, 0.16);
}
.search-result-item {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	text-align: left;
}
.search-result-icon {
	flex: 0 0 1.25rem;
	display: inline-flex;
	justify-content: center;
	padding-top: 0.15rem;
}
.search-result-body {
	min-width: 0;
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
}
.search-result-label {
	display: block;
	line-height: 1.35;
}
.search-result-context,
.search-dropdown-empty-state {
	display: block;
	margin-top: 0.2rem;
	line-height: 1.45;
	word-break: break-word;
}
</style>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import i18next, { LANG_STORAGE_KEY } from '@/i18n';
import SimpleBar from 'simplebar-vue';

import BrandLogo from '@/components/BrandLogo.vue';
import GlobalEventSelector from '@/components/GlobalEventSelector.vue';
import menuConfig from '@/json/menu.json';
import { apiLoadPublishedBulletins } from '@/apis/backend';
import { formatDateTime } from '@/utils/datetime';
import { mergeFeatureMenu } from '@/utils/feature-menu';
import flagTw from '@/assets/images/flags/tw.svg';
import flagCn from '@/assets/images/flags/cn.svg';
import flagJp from '@/assets/images/flags/jp.svg';
import flagKr from '@/assets/images/flags/kr.svg';
import flagUs from '@/assets/images/flags/us.svg';
import {
	applyAccountBrowserPreferences,
	loadAccountBrowserPreferences,
	updateAccountBrowserPreference,
} from '@/store/account-preferences';

const emit = defineEmits(['toggle-menu']);
const store = useStore();
const router = useRouter();
const route = useRoute();

// ─── 角色判斷 ───
const authState = computed(() => store.state.settings.auth);
const userRole = computed(() => {
	if (!authState.value.isLogin) return 'Visitor';
	return authState.value.accessRole || 'Guest';
});
const userName = computed(() => authState.value.accessName || '');
const featureAccess = computed(() => store.state.featureAccess);
const ogzuid = computed(() => authState.value.accessOUID || '');
const accountScopeId = computed(() => authState.value.accessUID || '');
const scopedAccountPreferences = computed(() => {
	const scopeId = `${accountScopeId.value}`.trim().toLowerCase();
	return store.state.settings.config.accountPreferences?.[scopeId] || null;
});
const showTopbarNotifications = computed(
	() => scopedAccountPreferences.value?.showTopbarNotifications !== false
);
const isDarkMode = computed(() => store.state.theme.themeMode === 'dark');

// ─── 搜尋 ───
const searchValue = ref('');
const searchOpen = ref(false);
const searchWrapper = ref(null);
const RECENT_MENU_SEARCH_KEY = 'Sapphire2_topbar_recent_menu_searches';
const RECENT_MENU_SEARCH_LIMIT = 5;

const normalizeLang = value => {
	if (!value) {
		return 'en';
	}
	const normalized = value.toLowerCase().replace('_', '-');
	const map = {
		en: 'en',
		'zh-tw': 'zh-tw',
		'zh-hant': 'zh-tw',
		'zh-hk': 'zh-tw',
		'zh-cn': 'zh-cn',
		'zh-hans': 'zh-cn',
		ja: 'ja',
		ko: 'ko',
	};
	return map[normalized] || 'en';
};

const labelForLocale = (item, locale = 'zh-tw') => {
	const labels = item?.label ?? {};
	return (
		labels[locale] || labels['zh-tw'] || labels.en || labels['zh-cn'] || ''
	);
};

const loadRecentSearchRouteNames = () => {
	try {
		const raw = localStorage.getItem(RECENT_MENU_SEARCH_KEY);
		if (!raw) {
			return [];
		}
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		console.warn('nav-bar, loadRecentSearchRouteNames failed', error);
		return [];
	}
};

const recentSearchRouteNames = ref(loadRecentSearchRouteNames());

const persistRecentSearchRouteNames = () => {
	localStorage.setItem(
		RECENT_MENU_SEARCH_KEY,
		JSON.stringify(recentSearchRouteNames.value)
	);
};

const addRecentSearchRoute = routeName => {
	recentSearchRouteNames.value = [
		routeName,
		...recentSearchRouteNames.value.filter(item => item !== routeName),
	].slice(0, RECENT_MENU_SEARCH_LIMIT);
	persistRecentSearchRouteNames();
};

const removeRecentSearchRoute = routeName => {
	recentSearchRouteNames.value = recentSearchRouteNames.value.filter(
		item => item !== routeName
	);
	persistRecentSearchRouteNames();
};

const searchLang = computed(() =>
	normalizeLang(
		selectedLanguage.value?.value || store?.state?.settings?.config?.lang
	)
);

const menuSearchEntries = computed(() => {
	const mergedMenu = mergeFeatureMenu(
		menuConfig?.Admin ?? [],
		featureAccess.value,
		routeName => router.hasRoute(routeName),
		ogzuid.value
	);

	const walk = (items, parents = []) => {
		if (!Array.isArray(items)) {
			return [];
		}

		return items.flatMap(item => {
			if (item?.visible === false) {
				return [];
			}

			const zhTwLabel = labelForLocale(item, 'zh-tw');
			const currentLabel = labelForLocale(item, searchLang.value);
			const nextParents = [
				...parents,
				{
					zhTw: zhTwLabel,
					current: currentLabel,
				},
			];

			if (item['route-name']) {
				// Search cannot bypass the same disabled state used by the sidebar.
				if (!router.hasRoute(item['route-name']) || item.disabled) {
					return [];
				}

				const parentTrail = parents.filter(
					parent => parent?.zhTw || parent?.current
				);

				return [
					{
						routeName: item['route-name'],
						labelZhTw: zhTwLabel,
						labelCurrent: currentLabel,
						contextZhTw: parentTrail
							.map(parent => parent.zhTw)
							.filter(Boolean)
							.join(' / '),
						contextCurrent: parentTrail
							.map(parent => parent.current)
							.filter(Boolean)
							.join(' / '),
						keywords: [
							zhTwLabel,
							currentLabel,
							labelForLocale(item, 'en'),
							labelForLocale(item, 'zh-cn'),
							...parentTrail.map(parent => parent.zhTw),
							...parentTrail.map(parent => parent.current),
						]
							.filter(Boolean)
							.map(value => value.toLowerCase()),
					},
				];
			}

			return walk(item.sub, nextParents);
		});
	};

	return walk(mergedMenu);
});

const menuSearchResults = computed(() => {
	const keyword = searchValue.value.trim().toLowerCase();
	if (!keyword) {
		return [];
	}

	return menuSearchEntries.value
		.filter(entry => entry.keywords.some(value => value.includes(keyword)))
		.sort((left, right) => {
			const leftStartsWith = left.labelZhTw
				.toLowerCase()
				.startsWith(keyword)
				? 0
				: 1;
			const rightStartsWith = right.labelZhTw
				.toLowerCase()
				.startsWith(keyword)
				? 0
				: 1;

			if (leftStartsWith !== rightStartsWith) {
				return leftStartsWith - rightStartsWith;
			}

			const leftIndex = left.labelZhTw.toLowerCase().indexOf(keyword);
			const rightIndex = right.labelZhTw.toLowerCase().indexOf(keyword);

			if (leftIndex !== rightIndex) {
				return leftIndex - rightIndex;
			}

			return left.labelZhTw.length - right.labelZhTw.length;
		})
		.slice(0, 8);
});

const recentSearchResults = computed(() =>
	recentSearchRouteNames.value
		.map(routeName =>
			menuSearchEntries.value.find(entry => entry.routeName === routeName)
		)
		.filter(Boolean)
);

const handleSearchInput = () => {
	searchOpen.value = true;
};

const openSearchDropdown = () => {
	searchOpen.value = true;
};

const clearSearch = () => {
	searchValue.value = '';
	searchOpen.value = false;
};

const handleSearchSubmit = () => {
	if (menuSearchResults.value.length > 0) {
		handleMenuSearchSelect(menuSearchResults.value[0]);
	}
};

const handleMenuSearchSelect = async result => {
	if (!result || result.disabled || !router.hasRoute(result.routeName)) {
		return;
	}

	addRecentSearchRoute(result.routeName);
	searchValue.value = '';
	searchOpen.value = false;
	await router.push({ name: result.routeName });
};

const handleBodyClick = event => {
	if (searchWrapper.value && !searchWrapper.value.contains(event.target)) {
		searchOpen.value = false;
	}
};

// ─── 漢堡選單 ───
const toggleHamburgerMenu = () => {
	const windowSize = document.documentElement.clientWidth;
	const layoutType = document.documentElement.getAttribute('data-layout');

	document.documentElement.setAttribute('data-sidebar-visibility', 'show');
	if (windowSize > 767) {
		document.querySelectorAll('.hamburger-icon').forEach(hamburger => {
			hamburger.classList.toggle('open');
		});
	}

	if (layoutType === 'vertical' || layoutType === 'semibox') {
		if (windowSize < 1025 && windowSize > 767) {
			document.documentElement.setAttribute('data-sidebar-size', 'sm');
		} else if (windowSize >= 1025) {
			const currentSize =
				document.documentElement.getAttribute('data-sidebar-size');
			document.documentElement.setAttribute(
				'data-sidebar-size',
				currentSize === 'lg' ? 'sm' : 'lg'
			);
		} else {
			document.documentElement.setAttribute('data-sidebar-size', 'lg');
		}
	}

	emit('toggle-menu');
};

// ─── 深色/淺色模式 ───
const toggleDarkMode = () => {
	const current = document.documentElement.getAttribute('data-bs-theme');
	const next = current === 'dark' ? 'light' : 'dark';
	document.documentElement.setAttribute('data-bs-theme', next);
	store.dispatch('layout/changeMode', { mode: next });
	store.dispatch('changeThemeMode', next);
	if (accountScopeId.value) {
		updateAccountBrowserPreference(
			store,
			accountScopeId.value,
			'themeMode',
			next
		);
	}
};

// ─── 全螢幕 ───
const toggleFullscreen = () => {
	document.body.classList.toggle('fullscreen-enable');
	if (
		!document.fullscreenElement &&
		!document.mozFullScreenElement &&
		!document.webkitFullscreenElement
	) {
		if (document.documentElement.requestFullscreen) {
			document.documentElement.requestFullscreen();
		} else if (document.documentElement.mozRequestFullScreen) {
			document.documentElement.mozRequestFullScreen();
		} else if (document.documentElement.webkitRequestFullscreen) {
			document.documentElement.webkitRequestFullscreen();
		}
	} else {
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else if (document.mozCancelFullScreen) {
			document.mozCancelFullScreen();
		} else if (document.webkitCancelFullScreen) {
			document.webkitCancelFullScreen();
		}
	}
};

// ─── 語言選擇器（5 語系 + i18next + localStorage） ───
const languages = [
	{
		flag: flagTw,
		labelKey: 'topbar.language.options.zh_tw',
		value: 'zh_tw',
	},
	{
		flag: flagCn,
		labelKey: 'topbar.language.options.zh_cn',
		value: 'zh_cn',
	},
	{ flag: flagUs, labelKey: 'topbar.language.options.en', value: 'en' },
	{ flag: flagJp, labelKey: 'topbar.language.options.ja', value: 'ja' },
	{ flag: flagKr, labelKey: 'topbar.language.options.ko', value: 'ko' },
];

/* 依登入帳號還原語系，訪客則沿用共用語系設定 */
const getSavedLanguage = () => {
	const saved = accountScopeId.value
		? loadAccountBrowserPreferences(store, accountScopeId.value).language
		: localStorage.getItem(LANG_STORAGE_KEY);
	return languages.find(l => l.value === saved) || languages[0];
};

const selectedLanguage = ref(getSavedLanguage());

const setLanguage = language => {
	selectedLanguage.value = language;
	if (accountScopeId.value) {
		updateAccountBrowserPreference(
			store,
			accountScopeId.value,
			'language',
			language.value
		);
	} else {
		localStorage.setItem(LANG_STORAGE_KEY, language.value);
	}
	i18next.changeLanguage(language.value);
	store.commit('changeSetting', {
		group: 'config',
		name: 'lang',
		value: language.value,
	});
};

// ─── Topbar 平台公告 ───
const notifications = ref([]);
const notificationsLoading = ref(false);
const notificationsError = ref(false);
const selectedNotification = ref(null);
const showNotificationModal = ref(false);
let notificationRequestId = 0;

const notificationCount = computed(() => notifications.value.length);

const formatNotificationTime = value => formatDateTime(value) || '-';

const closeNotification = () => {
	showNotificationModal.value = false;
	selectedNotification.value = null;
};

const openNotification = item => {
	selectedNotification.value = item;
	showNotificationModal.value = true;
};

const resetNotifications = () => {
	notifications.value = [];
	notificationsLoading.value = false;
	notificationsError.value = false;
	closeNotification();
};

const loadNotifications = async () => {
	const scopeOgzuid = ogzuid.value;
	if (!scopeOgzuid || !showTopbarNotifications.value) {
		notificationRequestId += 1;
		resetNotifications();
		return;
	}

	const requestId = ++notificationRequestId;
	notificationsLoading.value = true;
	notificationsError.value = false;
	try {
		const response = await apiLoadPublishedBulletins(scopeOgzuid);
		if (requestId !== notificationRequestId) return;
		const rows = response?.data?.dbo;
		notifications.value = Array.isArray(rows) ? rows : [];
	} catch (error) {
		if (requestId !== notificationRequestId) return;
		console.error('topbar-platform-bulletins, load, error=', error);
		notifications.value = [];
		notificationsError.value = true;
	} finally {
		if (requestId === notificationRequestId) {
			notificationsLoading.value = false;
		}
	}
};

// ─── 使用者資料下拉選單（依角色） ───
/* 留抵金佔位金額（API 整合延後） */
const creditBalance = ref(0);

const profileMenuItems = computed(() => {
	const role = userRole.value;
	void store.state.settings.config.lang;

	if (role === 'Staff') {
		return [
			{
				icon: 'mdi-account-circle',
				label: i18next.t('topbar.profile.basicProfile'),
				route: '/profile',
			},
			{
				icon: 'mdi-receipt',
				label: i18next.t('topbar.profile.billingHistory'),
				route: '/billing',
			},
			/*
			 * 暫停顯示：使用紀錄、幫助中心尚未開放，保留設定供日後啟用。
			{ icon: 'mdi-history', label: '使用紀錄', route: '/usage' },
			{ icon: 'mdi-lifebuoy', label: '幫助中心', route: '/help' },
			*/
			{ divider: true },
			{
				icon: 'mdi-cog-outline',
				label: i18next.t('topbar.profile.accountSettings'),
				route: '/account/settings',
			},
			{
				icon: 'mdi-logout',
				label: i18next.t('topbar.profile.signOut'),
				route: '/signout',
			},
		];
	}

	if (role === 'Member') {
		return [
			/*
			 * 暫停顯示：我的資料尚未開放，保留設定供日後啟用。
			{
				icon: 'mdi-account-circle',
				label: '我的資料',
				route: '/profile',
			},
			*/
			{
				icon: 'mdi-wallet',
				label: i18next.t('topbar.profile.creditBalance'),
				balance: creditBalance.value,
				route: '/credit',
			},
			/*
			 * 暫停顯示：使用紀錄、幫助中心尚未開放，保留設定供日後啟用。
			{ icon: 'mdi-history', label: '使用紀錄', route: '/usage' },
			{ icon: 'mdi-lifebuoy', label: '幫助中心', route: '/help' },
			*/
			{ divider: true },
			{
				icon: 'mdi-cog-outline',
				label: i18next.t('topbar.profile.accountSettings'),
				route: '/account/settings',
			},
			{
				icon: 'mdi-logout',
				label: i18next.t('topbar.profile.signOut'),
				route: '/signout',
			},
		];
	}

	/* Guest 預設 */
	return [
		/*
		 * 暫停顯示：我的資料、使用紀錄、幫助中心尚未開放，保留設定供日後啟用。
		{ icon: 'mdi-account-circle', label: '我的資料', route: '/profile' },
		{ icon: 'mdi-history', label: '使用紀錄', route: '/usage' },
		{ icon: 'mdi-lifebuoy', label: '幫助中心', route: '/help' },
		{ divider: true },
		*/
		{
			icon: 'mdi-cog-outline',
			label: i18next.t('topbar.profile.accountSettings'),
			route: '/account/settings',
		},
		{
			icon: 'mdi-logout',
			label: i18next.t('topbar.profile.signOut'),
			route: '/signout',
		},
	];
});

// ─── 生命週期 ───
watch(
	() => route.fullPath,
	() => {
		searchOpen.value = false;
	}
);

watch(
	() => store.state.settings.config.lang,
	language => {
		const option = languages.find(item => item.value === language);
		if (option) selectedLanguage.value = option;
	}
);

watch(accountScopeId, async scopeId => {
	if (!scopeId) return;
	const preferences = await applyAccountBrowserPreferences({
		store,
		orzuid: scopeId,
		i18next,
	});
	const option = languages.find(item => item.value === preferences.language);
	if (option) selectedLanguage.value = option;
});

watch(
	[ogzuid, showTopbarNotifications],
	() => {
		closeNotification();
		loadNotifications();
	},
	{ immediate: true }
);

onMounted(() => {
	document.body.addEventListener('click', handleBodyClick);
	/* 啟動時同步 i18next 語系 */
	i18next.changeLanguage(selectedLanguage.value.value);
});

onBeforeUnmount(() => {
	document.body.removeEventListener('click', handleBodyClick);
});
</script>
